import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from './cn';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * DataTable<T> — tabla reutilizable "estilo Mi Ganado".
 *
 * Replica las features que hoy solo tiene `AnimalList.tsx`:
 *   - Header sticky (queda fijo al hacer scroll vertical).
 *   - Columna de identificación "pinned" a la izquierda (queda fija al
 *     hacer scroll horizontal).
 *   - Selector de columnas (ocultar/mostrar) con persistencia en localStorage.
 *   - Vista mobile en cards (título + badge + pares etiqueta/valor).
 *
 * Es column-config driven: cada tabla define sus columnas con un `renderCell`
 * (que puede devolver Badges, íconos, inputs, selects, botones...). El estado
 * por fila (edición, selección, etc.) lo sigue manejando el componente padre.
 */

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface DataTableColumn<T> {
  /** Clave única de la columna. Sirve para visibilidad y como key de celda. */
  key: string;
  /** Contenido del encabezado (texto o nodo). */
  header: React.ReactNode;
  /** Render del contenido de cada celda. */
  renderCell: (row: T, index: number) => React.ReactNode;
  /** Encabezado clickeable para ordenar. El ordenamiento lo resuelve el padre. */
  sortable?: boolean;
  /** Pinea la columna a la izquierda (la "identificación"). */
  stickyLeft?: boolean;
  /** Ancho mínimo como clase tailwind, ej 'min-w-[120px]'. */
  minWidth?: string;
  /** Clases extra para las celdas del cuerpo. */
  cellClassName?: string;
  /** Clases extra para las celdas del encabezado. */
  headerClassName?: string;
  /** No se puede ocultar desde el selector de columnas (ej. la columna ID). */
  alwaysVisible?: boolean;
  /** Agrupación opcional en el selector de columnas. */
  group?: string;
  /** Habilita el filtro por columna (requiere `filterValue`). */
  filterable?: boolean;
  /**
   * Valor "crudo" de la columna para filtrar (NO el renderCell, que puede
   * devolver nodos React). Si devuelve null/undefined se trata como ''.
   */
  filterValue?: (row: T) => string | null | undefined;
  /**
   * Fuerza el tipo de filtro. Si no se pasa, se autodetecta: select cuando la
   * columna tiene pocos valores distintos (≤12), texto si no.
   */
  filterType?: 'text' | 'select';
}

export interface DataTableMobileConfig<T> {
  /** key de la columna que aparece como título bold del card. */
  titleColumn: string;
  /** Render opcional del título (por defecto usa `renderCell` de `titleColumn`). */
  renderTitle?: (row: T) => React.ReactNode;
  /** key de la columna que aparece como badge junto al título. */
  badgeColumn?: string;
  /** Botones/acciones del header del card. */
  renderActions?: (row: T) => React.ReactNode;
  /**
   * keys de columnas a mostrar en el cuerpo del card. Por defecto: todas las
   * visibles excepto titleColumn y badgeColumn.
   */
  secondaryColumns?: string[];
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  rowKey: (row: T) => string;
  /** Altura máxima antes de hacer scroll vertical. Default '60vh'. */
  maxHeight?: string;
  /** Altura mínima del cuerpo scrolleable: evita el colapso con 1-2 filas. */
  minHeight?: string;
  /** Ordenamiento (controlado por el padre). */
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSortChange?: (key: string, dir: 'asc' | 'desc') => void;
  /** Persistencia de columnas visibles en localStorage. */
  storageKey?: string;
  /** keys visibles por defecto (si no hay nada guardado). */
  defaultColumns?: string[];
  /** Config de vista mobile. Si no se pasa, se usa la tabla también en chico. */
  mobile?: DataTableMobileConfig<T>;
  /** Selección múltiple (checkbox). */
  selectable?: boolean;
  selectedKeys?: string[];
  onSelectionChange?: (keys: string[]) => void;
  /** Click en la fila completa. */
  onRowClick?: (row: T) => void;
  /** Clases extra por fila (ej. para resaltar filas con estado). */
  rowClassName?: (row: T) => string | undefined;
  /** Estado vacío. */
  emptyState?: React.ReactNode;
  /** Slot a la derecha del selector de columnas. */
  toolbar?: React.ReactNode;
  /**
   * Notifica cada cambio de los filtros por columna (set y clear). El estado
   * sigue siendo interno; sirve para que el padre reaccione al lote filtrado
   * (ej. delimitar una descarga).
   */
  onFiltersChange?: (filters: Record<string, string>) => void;
  /** Muestra el botón "Columnas" (selector de visibilidad). Default true. */
  showColumnSelector?: boolean;
  className?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extrae el ancho en px de una clase tipo 'min-w-[120px]' → 120. Default 120. */
function minWidthPx(minWidth?: string): number {
  if (!minWidth) return 120;
  const m = minWidth.match(/min-w-\[(\d+(?:\.\d+)?)px\]/);
  return m ? Number(m[1]) : 120;
}

// ---------------------------------------------------------------------------
// ColumnSelector
// ---------------------------------------------------------------------------

interface ColumnSelectorProps<T> {
  columns: DataTableColumn<T>[];
  visibleKeys: string[];
  defaultKeys: string[];
  onToggle: (key: string) => void;
  onReset: () => void;
}

function ColumnSelector<T>({ columns, visibleKeys, defaultKeys, onToggle, onReset }: ColumnSelectorProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // Agrupar manteniendo el orden de aparición.
  const groups = useMemo(() => {
    const map = new Map<string, DataTableColumn<T>[]>();
    for (const col of columns) {
      const g = col.group || '';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(col);
    }
    return Array.from(map.entries());
  }, [columns]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-3 py-1.5 text-sm font-medium text-ink transition-colors hover:bg-paper-2"
        title="Elegir columnas visibles"
      >
        <svg className="h-4 w-4 text-sage" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h7" />
        </svg>
        Columnas
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 w-60 max-h-80 overflow-y-auto rounded-lg border border-hairline bg-white p-2 shadow-lg">
          <div className="flex items-center justify-between px-1 pb-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-sage">Columnas visibles</span>
            <button
              type="button"
              onClick={onReset}
              className="text-xs font-medium text-forest hover:underline disabled:opacity-40"
              disabled={visibleKeys.length === defaultKeys.length && defaultKeys.every(k => visibleKeys.includes(k))}
            >
              Reset
            </button>
          </div>
          {groups.map(([groupName, cols]) => (
            <div key={groupName || '_default'} className={groupName ? 'mt-1' : ''}>
              {groupName && (
                <div className="px-1 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sage/70">{groupName}</div>
              )}
              {cols.map(col => {
                const checked = visibleKeys.includes(col.key);
                return (
                  <label
                    key={col.key}
                    className={cn(
                      'flex items-center gap-2 rounded px-1.5 py-1.5 text-sm text-ink',
                      col.alwaysVisible ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-paper-2',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={col.alwaysVisible}
                      onChange={() => onToggle(col.key)}
                      className="h-4 w-4 rounded border-hairline text-forest focus:ring-forest/40"
                    />
                    <span className="truncate">{typeof col.header === 'string' ? col.header : col.key}</span>
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DataTable
// ---------------------------------------------------------------------------

export function DataTable<T>({
  columns,
  data,
  rowKey,
  maxHeight = '60vh',
  minHeight,
  sortBy,
  sortDirection,
  onSortChange,
  storageKey,
  defaultColumns,
  mobile,
  selectable = false,
  selectedKeys = [],
  onSelectionChange,
  onRowClick,
  rowClassName,
  emptyState,
  toolbar,
  onFiltersChange,
  showColumnSelector = true,
  className,
}: DataTableProps<T>) {
  const { isMobile } = useIsMobile();

  // ----- Columnas visibles (con persistencia) -----
  const allKeys = useMemo(() => columns.map(c => c.key), [columns]);
  const fallbackDefault = useMemo(
    () => (defaultColumns && defaultColumns.length > 0 ? defaultColumns : allKeys),
    [defaultColumns, allKeys],
  );

  const [visibleKeys, setVisibleKeys] = useState<string[]>(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed) && parsed.length > 0) {
            // Filtrar claves que ya no existen (schema cambió).
            return parsed.filter((k: string) => allKeys.includes(k));
          }
        }
      } catch {
        /* ignore */
      }
    }
    return fallbackDefault;
  });

  // Re-leer defaults si cambia el set de columnas o el storageKey.
  useEffect(() => {
    setVisibleKeys(prev => {
      const filtered = prev.filter(k => allKeys.includes(k));
      return filtered.length > 0 ? filtered : fallbackDefault;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allKeys.join('|'), storageKey]);

  const toggleColumn = (key: string) => {
    setVisibleKeys(prev => {
      // Las alwaysVisible no se pueden ocultar.
      const col = columns.find(c => c.key === key);
      if (col?.alwaysVisible && prev.includes(key)) return prev;
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          /* ignore */
        }
      }
      return next;
    });
  };

  const resetColumns = () => {
    setVisibleKeys(fallbackDefault);
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(fallbackDefault));
      } catch {
        /* ignore */
      }
    }
  };

  // El selector de columnas se muestra (salvo que se deshabilite vía prop)
  // siempre que haya más de una columna (independiente de si hay persistencia
  // en localStorage o no).
  const columnSelectorVisible = showColumnSelector && columns.length > 1;

  // Columnas finales (en orden) según visibilidad.
  const visibleColumns = useMemo(
    () => columns.filter(c => visibleKeys.includes(c.key)),
    [columns, visibleKeys],
  );

  // ----- Offsets acumulados para columnas pinned a la izquierda -----
  // Para cada columna stickyLeft, calculamos `left` en px sumando los anchos
  // de las columnas sticky anteriores (igual que AnimalList).
  const stickyOffsets = useMemo(() => {
    const offsets = new Map<string, number>();
    let acc = 0;
    // La columna de selección (checkbox) se renderiza PRIMERO en el DOM y debe
    // quedar a la izquierda del todo; las stickyLeft se desplazan a su derecha.
    // Si la dejáramos con offset = ancho de la caravana, una caravana con texto
    // largo (más ancha que su min-width) la taponeaba e interceptaba los clicks.
    if (selectable) {
      offsets.set('__selection__', 0);
      acc += isMobile ? 48 : 60; // min-w-[48px] sm:min-w-[60px]
    }
    for (const col of columns) {
      if (!col.stickyLeft) continue;
      offsets.set(col.key, acc);
      acc += minWidthPx(col.minWidth);
    }
    return offsets;
  }, [columns, selectable, isMobile]);

  // ----- Filtros por columna -----
  // Estado interno: { [columnKey]: valorDeFiltro }. Texto = substring
  // case-insensitive; select = match exacto. Las columnas filtrables son las
  // que definen `filterValue` (el valor crudo, no el renderCell).
  const [filters, setFilters] = useState<Record<string, string>>({});
  const filterableColumns = useMemo(
    () => columns.filter(c => (c.filterable || c.filterValue) && c.filterValue),
    [columns],
  );

  // Valores únicos por columna (para el autodetect select/texto y las options).
  const uniqueValues = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const col of filterableColumns) {
      const accessor = col.filterValue!;
      const vals = new Set<string>();
      for (const row of data) {
        const v = accessor(row);
        if (v != null && v !== '') vals.add(v);
      }
      map.set(col.key, Array.from(vals).sort((a, b) => a.localeCompare(b, 'es')));
    }
    return map;
  }, [filterableColumns, data]);

  const filterTypeFor = (col: DataTableColumn<T>): 'text' | 'select' =>
    col.filterType ?? ((uniqueValues.get(col.key)?.length ?? 0) <= 12 ? 'select' : 'text');

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  const filteredData = useMemo(() => {
    if (activeFilterCount === 0) return data;
    const active = filterableColumns
      .map(col => ({ col, q: (filters[col.key] ?? '').trim().toLowerCase() }))
      .filter(a => a.q !== '');
    return data.filter(row =>
      active.every(({ col, q }) => {
        const v = (col.filterValue!(row) ?? '').toLowerCase();
        // select = match exacto; texto = substring.
        return filterTypeFor(col) === 'select' ? v === q : v.includes(q);
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filters, filterableColumns, uniqueValues]);

  const setFilter = (key: string, value: string) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    onFiltersChange?.(next);
  };

  const clearFilters = () => {
    setFilters({});
    onFiltersChange?.({});
  };

  // ----- Selección (sobre los datos filtrados) -----
  const selectAllData = activeFilterCount > 0 ? filteredData : data;
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const allOnPageSelected = selectAllData.length > 0 && selectAllData.every(r => selectedSet.has(rowKey(r)));
  const someOnPageSelected = selectAllData.some(r => selectedSet.has(rowKey(r)));

  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;
    const pageKeys = selectAllData.map(rowKey);
    if (checked) {
      onSelectionChange(Array.from(new Set([...selectedKeys, ...pageKeys])));
    } else {
      onSelectionChange(selectedKeys.filter(k => !pageKeys.includes(k)));
    }
  };

  const handleSelectRow = (key: string, checked: boolean) => {
    if (!onSelectionChange) return;
    if (checked) onSelectionChange(Array.from(new Set([...selectedKeys, key])));
    else onSelectionChange(selectedKeys.filter(k => k !== key));
  };

  // ----- Helpers de render de sort -----
  const handleSort = (col: DataTableColumn<T>) => {
    if (!onSortChange || !col.sortable) return;
    const currentDir = sortBy === col.key ? sortDirection : null;
    if (currentDir === 'asc') onSortChange(col.key, 'desc');
    else if (currentDir === 'desc') onSortChange('', 'asc');
    else onSortChange(col.key, 'asc');
  };

  // ----- Helpers de render de filtros -----
  const headerText = (col: DataTableColumn<T>): string =>
    typeof col.header === 'string' ? col.header : col.key;

  const renderFilterInput = (col: DataTableColumn<T>) => {
    if (!col.filterValue) return null;
    const value = filters[col.key] ?? '';
    const base =
      'w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm text-ink focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20';
    if (filterTypeFor(col) === 'select') {
      return (
        <select value={value} onChange={e => setFilter(col.key, e.target.value)} className={base}>
          <option value="">Todos</option>
          {(uniqueValues.get(col.key) ?? []).map(v => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        type="text"
        value={value}
        onChange={e => setFilter(col.key, e.target.value)}
        placeholder="Filtrar…"
        className={base}
      />
    );
  };

  const visibleFilterableColumns = filterableColumns.filter(c => visibleKeys.includes(c.key));

  const activeFiltersBar = activeFilterCount > 0 ? (
    <div className="mb-2 flex items-center justify-end gap-3 text-xs text-sage">
      <span>
        {filteredData.length} de {data.length} filas
      </span>
      <button type="button" onClick={clearFilters} className="font-medium text-forest hover:underline">
        Limpiar filtros
      </button>
    </div>
  ) : null;

  // ----- Empty state -----
  if (data.length === 0 && emptyState) {
    return (
      <div>
        {toolbar || columnSelectorVisible ? (
          <div className="mb-3 flex items-center justify-end gap-2">
            {toolbar}
            {columnSelectorVisible && (
              <ColumnSelector
                columns={columns}
                visibleKeys={visibleKeys}
                defaultKeys={fallbackDefault}
                onToggle={toggleColumn}
                onReset={resetColumns}
              />
            )}
          </div>
        ) : null}
        {emptyState}
      </div>
    );
  }

  // ----- Vista mobile en cards -----
  const showMobileCards = isMobile && !!mobile;
  if (showMobileCards && mobile) {
    const titleCol = columns.find(c => c.key === mobile.titleColumn);
    const bodyKeys =
      mobile.secondaryColumns ??
      visibleColumns.filter(c => c.key !== mobile.titleColumn && c.key !== mobile.badgeColumn).map(c => c.key);
    const bodyCols = bodyKeys.map(k => columns.find(c => c.key === k)).filter(Boolean) as DataTableColumn<T>[];

    return (
      <div className={cn('space-y-3', className)}>
        {(toolbar || columnSelectorVisible) && (
          <div className="flex items-center justify-end gap-2">
            {toolbar}
            {columnSelectorVisible && (
              <ColumnSelector
                columns={columns}
                visibleKeys={visibleKeys}
                defaultKeys={fallbackDefault}
                onToggle={toggleColumn}
                onReset={resetColumns}
              />
            )}
          </div>
        )}

        {/* Panel de filtros por columna (mobile) */}
        {visibleFilterableColumns.length > 0 && (
          <div className="grid grid-cols-2 gap-2 rounded-md border border-hairline bg-white p-3">
            {visibleFilterableColumns.map(col => (
              <label key={col.key} className="flex min-w-0 flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-sage">
                  {headerText(col)}
                </span>
                {renderFilterInput(col)}
              </label>
            ))}
          </div>
        )}

        {activeFiltersBar}

        {selectable && (
          <div className="flex items-center justify-between rounded-md border border-hairline bg-white px-3 py-2">
            <label className="flex items-center gap-2 text-sm font-medium text-ink">
              <input
                type="checkbox"
                checked={allOnPageSelected}
                ref={el => {
                  if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected;
                }}
                onChange={e => handleSelectAll(e.target.checked)}
                className="h-4 w-4 rounded border-hairline text-forest focus:ring-forest/40"
              />
              Seleccionar todos ({selectAllData.length})
            </label>
          </div>
        )}

        {filteredData.map((row, idx) => {
          const key = rowKey(row);
          const isSelected = selectedSet.has(key);
          return (
            <div
              key={key}
              className={cn(
                'overflow-hidden rounded-xl border bg-white shadow-sm transition-colors',
                isSelected ? 'border-forest ring-1 ring-forest/30' : 'border-hairline hover:border-forest/40',
              )}
            >
              <div className="flex items-center justify-between gap-2 border-b border-hairline bg-paper-2/60 px-3 py-2.5">
                <div className="flex min-w-0 items-center gap-2">
                  {selectable && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={e => handleSelectRow(key, e.target.checked)}
                      className="h-4 w-4 flex-shrink-0 rounded border-hairline text-forest focus:ring-forest/40"
                    />
                  )}
                  <span className="break-all leading-tight text-base font-bold text-forest">
                    {mobile.renderTitle ? mobile.renderTitle(row) : titleCol ? titleCol.renderCell(row, idx) : key}
                  </span>
                </div>
                {mobile.renderActions && <div className="flex flex-shrink-0 items-center gap-1">{mobile.renderActions(row)}</div>}
              </div>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 px-3 py-2.5 text-sm">
                {bodyCols.map(col => (
                  <div key={col.key} className="flex min-w-0 flex-col">
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-sage">{col.header}</dt>
                    <dd className="truncate font-medium text-ink">{col.renderCell(row, idx)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          );
        })}
      </div>
    );
  }

  // ----- Vista desktop/tablet: tabla con sticky header + columna pinned -----
  return (
    <div className={cn(className)}>
      {(toolbar || columnSelectorVisible) && (
        <div className="mb-3 flex items-center justify-end gap-2">
          {toolbar}
          {columnSelectorVisible && (
            <ColumnSelector
              columns={columns}
              visibleKeys={visibleKeys}
              defaultKeys={fallbackDefault}
              onToggle={toggleColumn}
              onReset={resetColumns}
            />
          )}
        </div>
      )}

      {activeFiltersBar}

      <div className="relative isolate overflow-hidden rounded-lg border border-hairline bg-white shadow-card">
        <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight, minHeight }}>
          <table className="min-w-full divide-y divide-hairline">
            <thead className="sticky top-0 z-20 bg-paper-2 shadow-sm">
              <tr>
                {selectable && (
                  <th
                    className="sticky top-0 z-30 min-w-[48px] border-b border-hairline bg-paper-2 px-2 sm:px-3 py-3 sm:min-w-[60px]"
                    style={{ left: stickyOffsets.get('__selection__') ?? 0 }}
                  >
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      ref={el => {
                        if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected;
                      }}
                      onChange={e => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-hairline text-forest focus:ring-forest/40"
                    />
                  </th>
                )}
                {visibleColumns.map(col => {
                  const isSticky = col.stickyLeft;
                  const baseTh = cn(
                    'whitespace-nowrap px-3 sm:px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink/70 border-b border-hairline',
                    isSticky && 'sticky top-0 z-30 bg-paper-2',
                    !isSticky && 'bg-paper-2',
                    col.sortable && 'cursor-pointer hover:bg-paper-2/70',
                    col.minWidth,
                    col.headerClassName,
                  );
                  const style = isSticky ? { left: stickyOffsets.get(col.key) ?? 0 } : undefined;
                  const content = col.sortable ? (
                    <span className="inline-flex items-center gap-1">
                      <span>{col.header}</span>
                      <span className="shrink-0 text-sage">
                        {sortBy === col.key ? (sortDirection === 'asc' ? '↑' : '↓') : <span className="text-sage/40">↕</span>}
                      </span>
                    </span>
                  ) : (
                    col.header
                  );
                  return (
                    <th key={col.key} scope="col" className={baseTh} style={style} onClick={() => handleSort(col)}>
                      {content}
                    </th>
                  );
                })}
              </tr>

              {/* Fila de filtros por columna (solo si alguna columna es filtrable) */}
              {filterableColumns.length > 0 && (
                <tr>
                  {selectable && (
                    <th className="border-b border-hairline bg-paper-2 px-2 py-2 sm:px-3" />
                  )}
                  {visibleColumns.map(col => {
                    const isSticky = col.stickyLeft;
                    return (
                      <th
                        key={`filter-${col.key}`}
                        scope="col"
                        className={cn(
                          'px-3 sm:px-6 py-2 border-b border-hairline bg-paper-2/70',
                          isSticky && 'sticky z-10 bg-paper-2',
                          col.minWidth,
                        )}
                        style={isSticky ? { left: stickyOffsets.get(col.key) ?? 0 } : undefined}
                      >
                        {renderFilterInput(col)}
                      </th>
                    );
                  })}
                </tr>
              )}
            </thead>
            <tbody className="divide-y divide-hairline bg-white">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + (selectable ? 1 : 0)} className="px-6 py-12 text-center text-sm text-sage">
                    {activeFilterCount > 0
                      ? 'Sin resultados con los filtros aplicados'
                      : 'Sin resultados'}
                  </td>
                </tr>
              ) : (
                filteredData.map((row, idx) => {
                  const key = rowKey(row);
                  return (
                    <tr
                      key={key}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      className={cn('transition-colors duration-200 hover:bg-paper-2/60', onRowClick && 'cursor-pointer', rowClassName?.(row))}
                    >
                      {selectable && (
                        <td
                          className="sticky z-10 min-w-[48px] bg-white px-2 sm:px-3 py-2 sm:min-w-[60px] hover:bg-paper-2/60"
                          style={{ left: stickyOffsets.get('__selection__') ?? 0 }}
                          onClick={e => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSet.has(key)}
                            onChange={e => handleSelectRow(key, e.target.checked)}
                            className="h-4 w-4 rounded border-hairline text-forest focus:ring-forest/40"
                          />
                        </td>
                      )}
                      {visibleColumns.map(col => {
                        const isSticky = col.stickyLeft;
                        return (
                          <td
                            key={col.key}
                            className={cn(
                              'whitespace-nowrap px-3 sm:px-6 py-3 sm:py-4 text-sm text-ink',
                              isSticky && 'sticky z-10 bg-white hover:bg-paper-2/60',
                              col.cellClassName,
                            )}
                            style={isSticky ? { left: stickyOffsets.get(col.key) ?? 0 } : undefined}
                          >
                            {col.renderCell(row, idx)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DataTable;
